import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Church, 
  Users, 
  ChevronDown, 
  ChevronRight,
  LogOut,
  Shield,
  MapPin,
  Globe
} from 'lucide-react';
import api from '../services/api';

export default function AdminPanel() {
  const [user, setUser] = useState(null);
  const [dioceses, setDioceses] = useState([]);
  const [paroquias, setParoquias] = useState({});
  const [comunidades, setComunidades] = useState({});
  const [grupos, setGrupos] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingStates, setLoadingStates] = useState({});
  const [error, setError] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const navigate = useNavigate();

  useEffect(() => {

    let mounted = true;
    const checkAndLoad = async () => {
      try {
        // Verificar se há token
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        // Buscar dados do usuário via API
        const userResponse = await api.get('/auth/me');
        const userData = userResponse.data;

        // Verificar se o usuário tem role de admin
        if (!userData.roles || !userData.roles.includes('admin')) {
          navigate('/login');
          return;
        }

        // Salva snapshot do contexto original se não existir
        if (!localStorage.getItem('user_origem')) {
          localStorage.setItem('user_origem', JSON.stringify(userData));
        }

        if (mounted) {
          setUser(userData);
          const response = await api.get('/dioceses');
          setDioceses(response.data || []);
        }
      } catch (err) {
        console.error('Erro ao verificar acesso admin ou carregar dioceses:', err);
        setError('Erro ao carregar dados');
        navigate('/login');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    setLoading(true);
    checkAndLoad();
    return () => { mounted = false; };
  }, [navigate]);

  const loadParoquias = async (dioceseId) => {
    // Se já carregamos as paróquias desta diocese, não carrega novamente
    if (paroquias[dioceseId]) {
      return;
    }
    
    setLoadingStates(prev => ({ ...prev, [`dioceses-${dioceseId}`]: true }));
    
    try {
      const response = await api.get(`/paroquias?dioceseId=${dioceseId}`);
      setParoquias(prev => ({ ...prev, [dioceseId]: response.data || [] }));
    } catch (err) {
      setError('Erro ao carregar paróquias');
      console.error('Erro ao carregar paróquias:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, [`dioceses-${dioceseId}`]: false }));
    }
  };

  const loadComunidades = async (paroquiaId) => {
    // Se já carregamos as comunidades desta paróquia, não carrega novamente
    if (comunidades[paroquiaId]) {
      return;
    }
    
    setLoadingStates(prev => ({ ...prev, [`paroquias-${paroquiaId}`]: true }));
    
    try {
      const response = await api.get(`/comunidades?paroquiaId=${paroquiaId}`);
      setComunidades(prev => ({ ...prev, [paroquiaId]: response.data || [] }));
    } catch (err) {
      setError('Erro ao carregar comunidades');
      console.error('Erro ao carregar comunidades:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, [`paroquias-${paroquiaId}`]: false }));
    }
  };

  const loadGrupos = async (comunidadeId) => {
    // Se já carregamos os grupos desta comunidade, não carrega novamente
    if (grupos[comunidadeId]) {
      return;
    }
    
    setLoadingStates(prev => ({ ...prev, [`comunidades-${comunidadeId}`]: true }));
    
    try {
      const response = await api.get(`/grupos?comunidadeId=${comunidadeId}`);
      setGrupos(prev => ({ ...prev, [comunidadeId]: response.data || [] }));
    } catch (err) {
      setError('Erro ao carregar grupos');
      console.error('Erro ao carregar grupos:', err);
    } finally {
      setLoadingStates(prev => ({ ...prev, [`comunidades-${comunidadeId}`]: false }));
    }
  };

  const handleDioceseSelect = async (diocese) => {
    const isExpanded = expandedItems[`diocese-${diocese.id}`];
    
    if (!isExpanded) {
      // Carregar paróquias antes de expandir
      await loadParoquias(diocese.id);
    }
    
    toggleExpanded(`diocese-${diocese.id}`);
  };

  const handleParoquiaSelect = async (paroquia) => {
    const isExpanded = expandedItems[`paroquia-${paroquia.id}`];
    
    if (!isExpanded) {
      // Carregar comunidades antes de expandir
      await loadComunidades(paroquia.id);
    }
    
    toggleExpanded(`paroquia-${paroquia.id}`);
  };

  const handleComunidadeSelect = async (comunidade) => {
    const isExpanded = expandedItems[`comunidade-${comunidade.id}`];
    
    if (!isExpanded) {
      // Carregar grupos antes de expandir
      await loadGrupos(comunidade.id);
    }
    
    toggleExpanded(`comunidade-${comunidade.id}`);
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handleAccessGroup = (diocese, paroquia, comunidade, grupo) => {
    // Função para gerar slugs
    const slugify = (text) => String(text || '')
      .normalize('NFD')
      .replace(/[^\w\s-]/g, '')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/--+/g, '-');

    // Extrai nomes com fallback
    const dioceseNome = diocese.nome || diocese.name || diocese.title || `diocese-${diocese.id}`;
    const paroquiaNome = paroquia.nome || paroquia.name || paroquia.title || `paroquia-${paroquia.id}`;
    const comunidadeNome = comunidade.nome || comunidade.name || comunidade.title || `comunidade-${comunidade.id}`;
    const grupoTipo = grupo.tipo || grupo.type || grupo.nome || grupo.name || grupo.title || `grupo-${grupo.id}`;

    // Gera slugs
    const dioceseSlug = slugify(dioceseNome);
    const paroquiaSlug = slugify(paroquiaNome);
    const comunidadeSlug = slugify(comunidadeNome);
    const grupoSlug = slugify(grupoTipo);

    if (!dioceseSlug || !paroquiaSlug || !comunidadeSlug || !grupoSlug) {
      alert('Erro: Não foi possível gerar a URL. Verifique se todos os dados estão completos.');
      return;
    }

    // Mapeamento dos tipos de grupo para rotas
    const routeMap = {
      'coordenacao': 'coordenacao',
      'coordenador': 'coordenacao',
    };
    const tipoLower = grupoTipo.toLowerCase();
    let routeSuffix = '';
    if (routeMap[tipoLower]) {
      routeSuffix = routeMap[tipoLower];
    } else {
      for (const key of Object.keys(routeMap)) {
        if (tipoLower.includes(key)) {
          routeSuffix = routeMap[key];
          break;
        }
      }
    }

    const destino = routeSuffix
      ? `/diocese/${dioceseSlug}/paroquia/${paroquiaSlug}/comunidade/${comunidadeSlug}/tipo/${grupoSlug}/${routeSuffix}`
      : `/diocese/${dioceseSlug}/paroquia/${paroquiaSlug}/comunidade/${comunidadeSlug}/tipo/${grupoSlug}`;

    // Salva dados temporários no localStorage para navegação
    const tempUser = {
      ...user,
      diocese: dioceseNome,
      paroquia: paroquiaNome,
      comunidade: comunidadeNome,
      tipo: grupoTipo,
      diocese_id: diocese.id,
      paroquia_id: paroquia.id,
      comunidade_id: comunidade.id,
      grupo_id: grupo.id,
      roles: [...(user.roles || []), 'admin']
    };
    localStorage.setItem('user', JSON.stringify(tempUser));
    navigate(destino);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-lg text-gray-500 mt-4">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-20">
        <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center py-3 gap-2 sm:gap-0">
            <div className="flex items-center w-full sm:w-auto justify-center sm:justify-start">
              <Shield className="h-8 w-8 text-blue-600 mr-2 sm:mr-3" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 text-center sm:text-left">Painel Administrativo</h1>
                <p className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                  Bem-vindo(a), {user?.name} | Coordenador
                </p>
              </div>
            </div>
            <div className="flex gap-1 sm:gap-2 items-center w-full sm:w-auto justify-center sm:justify-end">
              {/* Botão Ir para minha área */}
              <button
                className="px-3 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                onClick={() => {
                  // Sempre usa o snapshot do contexto original do admin
                  const userOrigem = JSON.parse(localStorage.getItem('user_origem'));
                  if (
                    userOrigem &&
                    userOrigem.diocese &&
                    userOrigem.paroquia &&
                    userOrigem.comunidade &&
                    userOrigem.tipo
                  ) {
                    // Atualiza o contexto atual para garantir que a navegação funcione
                    localStorage.setItem('user', JSON.stringify(userOrigem));
                    const slugify = (text) =>
                      (text || '')
                        .normalize('NFD')
                        .replace(/\u0300-\u036f/g, '')
                        .toLowerCase()
                        .replace(/\s+/g, '-')
                        .replace(/[^\w-]+/g, '')
                        .replace(/--+/g, '-');
                    const path = `/diocese/${slugify(userOrigem.diocese)}/paroquia/${slugify(userOrigem.paroquia)}/comunidade/${slugify(userOrigem.comunidade)}/tipo/${slugify(userOrigem.tipo)}`;
                    navigate(path);
                  } else {
                    navigate('/admin');
                  }
                }}
              >
                Ir para minha área
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sair
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-full mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <div className="p-3 sm:p-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6">
              Navegar por Estrutura Organizacional
            </h2>
            {/* Dioceses */}
            <div className="space-y-3 sm:space-y-4">
              {dioceses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Nenhuma diocese encontrada
                </div>
              ) : (
                dioceses.map((diocese) => (
                  <div key={diocese.id} className="border rounded-lg">
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleDioceseSelect(diocese)}
                    >
                      <div className="flex items-center">
                        <Globe className="h-5 w-5 text-blue-600 mr-3" />
                        <span className="font-medium text-gray-900">
                          {diocese.nome || diocese.name || 'Nome não encontrado'}
                        </span>
                      </div>
                      {expandedItems[`diocese-${diocese.id}`] ? (
                        <ChevronDown className="h-5 w-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-gray-500" />
                      )}
                    </div>

                    {/* Paróquias */}
                    {expandedItems[`diocese-${diocese.id}`] && (
                      <div className="pl-6 pb-4">
                        {loadingStates[`dioceses-${diocese.id}`] ? (
                          <div className="text-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="text-sm text-gray-500 mt-2">Carregando paróquias...</p>
                          </div>
                        ) : !paroquias[diocese.id] || paroquias[diocese.id].length === 0 ? (
                          <div className="text-center py-4 text-gray-500 text-sm">
                            Nenhuma paróquia encontrada para esta diocese
                          </div>
                        ) : (
                          paroquias[diocese.id].map((paroquia) => (
                            <div key={paroquia.id} className="border-l-2 border-gray-200 ml-2">
                              <div
                                className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 ml-4"
                                onClick={() => handleParoquiaSelect(paroquia)}
                              >
                                <div className="flex items-center">
                                  <Church className="h-4 w-4 text-green-600 mr-3" />
                                  <span className="text-gray-800">
                                    {paroquia.nome || paroquia.name || 'Nome não encontrado'}
                                  </span>
                                </div>
                                {expandedItems[`paroquia-${paroquia.id}`] ? (
                                  <ChevronDown className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-gray-500" />
                                )}
                              </div>

                              {/* Comunidades */}
                              {expandedItems[`paroquia-${paroquia.id}`] && (
                                <div className="pl-6">
                                  {loadingStates[`paroquias-${paroquia.id}`] ? (
                                    <div className="text-center py-4">
                                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                                      <p className="text-sm text-gray-500 mt-2">Carregando comunidades...</p>
                                    </div>
                                  ) : !comunidades[paroquia.id] || comunidades[paroquia.id].length === 0 ? (
                                    <div className="text-center py-4 text-gray-500 text-sm">
                                      Nenhuma comunidade encontrada para esta paróquia
                                    </div>
                                  ) : (
                                    comunidades[paroquia.id].map((comunidade) => (
                                      <div key={comunidade.id} className="border-l-2 border-gray-200 ml-2">
                                        <div
                                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 ml-4"
                                          onClick={() => handleComunidadeSelect(comunidade)}
                                        >
                                          <div className="flex items-center">
                                            <MapPin className="h-4 w-4 text-purple-600 mr-3" />
                                            <span className="text-gray-700">
                                              {comunidade.nome || comunidade.name || 'Nome não encontrado'}
                                            </span>
                                          </div>
                                          {expandedItems[`comunidade-${comunidade.id}`] ? (
                                            <ChevronDown className="h-4 w-4 text-gray-500" />
                                          ) : (
                                            <ChevronRight className="h-4 w-4 text-gray-500" />
                                          )}
                                        </div>

                                        {/* Grupos */}
                                        {expandedItems[`comunidade-${comunidade.id}`] && (
                                          <div className="pl-6">
                                            {loadingStates[`comunidades-${comunidade.id}`] ? (
                                              <div className="text-center py-4">
                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                                                <p className="text-sm text-gray-500 mt-2">Carregando grupos...</p>
                                              </div>
                                            ) : !grupos[comunidade.id] || grupos[comunidade.id].length === 0 ? (
                                              <div className="text-center py-4 text-gray-500 text-sm">
                                                Nenhum grupo encontrado para esta comunidade
                                              </div>
                                            ) : (
                                              grupos[comunidade.id].map((grupo) => (
                                                <div key={grupo.id} className="border-l-2 border-gray-200 ml-2">
                                                  <div className="flex items-center justify-between p-3 ml-4">
                                                    <div className="flex items-center">
                                                      <Users className="h-4 w-4 text-orange-600 mr-3" />
                                                      <span className="text-gray-600">
                                                        {grupo.tipo || grupo.type || grupo.nome || grupo.name || 'Tipo não encontrado'}
                                                      </span>
                                                    </div>
                                                    <button
                                                      onClick={() => handleAccessGroup(diocese, paroquia, comunidade, grupo)}
                                                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                                    >
                                                      Acessar
                                                    </button>
                                                  </div>
                                                </div>
                                              ))
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    ))
                                  )}
                                </div>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
